import React from 'react';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

export default function AboutFAQ() {
  // FAQ data
  const faqs: FAQItem[] = [
    {
      id: 'faq-1',
      question: 'What kind of clients do you work with?',
      answer:
        "I work with expert service providers, consultants, agencies, and serious domain owners who want to turn a strong domain into a functioning, client-getting business. If you already have paying clients (or clear demand) and want your brand and website to finally match the quality of your work, we're probably a good fit.",
    },
    {
      id: 'faq-2',
      question: 'Do I have to buy a domain from Attain Domains to work with you?',
      answer:
        "No. If you already own a great domain, we can build around that. Attain Domains exists because I have a curated portfolio of premium names that are perfect for service businesses - but the same launch protocol applies whether we're using one of my domains or one you already own.",
    },
    {
      id: 'faq-3',
      question: 'What if I already have a website?',
      answer:
        "That's common. We'll look at what's working, what isn't, and decide whether to refine what you have or rebuild from a clean, conversion-first foundation. The goal isn't to redesign for the sake of it - it's to create a site that makes it easy for the right clients to understand you, trust you, and book with you.",
    },
    {
      id: 'faq-4',
      question: 'How long does a typical project take?',
      answer:
        "Most full brand + website launches take a few weeks to a couple of months, depending on scope and how quickly we can make decisions together. I'll give you a clear timeline after we map your project, and I won't bury you in project management - the whole point is to make this feel focused and manageable while we move fast.",
    },
    {
      id: 'faq-5',
      question: 'How involved do I need to be?',
      answer:
        "You don't need to write copy or design anything, but I do need your input on strategy, offers, and what \"winning\" looks like for you. We'll do a focused strategy call at the start, agree on the plan, and then I'll do the heavy lifting with check-ins at key milestones.",
    },
    {
      id: 'faq-6',
      question: 'What about pricing?',
      answer:
        "Because every project is a bit different, I don't list one-size-fits-all packages on the About page. On our first call, we'll map your goals and I'll recommend a scope that fits your stage of business. You'll see clear pricing and options on the Services page before you decide anything.",
    },
    {
      id: 'faq-7',
      question: 'Who owns the assets when we are done?',
      answer:
        "You do. When the project is complete and paid for, you own your domain (if you purchased it), your brand assets, and your website. I'll help you set things up in a way that you or your team can manage going forward - and I'm here if you want ongoing support.",
    },
    {
      id: 'faq-8',
      question: 'How do we get started?',
      answer:
        'The easiest way is to book a consult and come prepared to talk about your domain, your offers, and what a "win" looks like in the next 6-12 months. From there, we will decide together if it makes sense to move into a full build. You can see current options on the Services page and in the final CTA section below.',
    },
  ];

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut' as const,
      },
    },
  };

  const headingVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut' as const,
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const headingItemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' as const },
    },
  };

  const accordionVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' as const },
    },
  };

  return (
    <motion.section
      id="faq"
      aria-label="Frequently asked questions about working with Attain Domains"
      variants={sectionVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      className="relative w-full py-20 md:py-24 bg-gradient-to-b from-slate-50 via-white to-sky-50 overflow-hidden"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Heading block */}
          <motion.div
            variants={headingVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="text-center mb-12 md:mb-16"
          >
            <motion.div variants={headingItemVariants}>
              <p className="text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase mb-4">
                QUESTIONS, ANSWERED
              </p>
            </motion.div>

            <motion.div variants={headingItemVariants}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-6">
                A few things people usually ask before we work together.
              </h2>
            </motion.div>

            <motion.div variants={headingItemVariants}>
              <p className="text-base md:text-lg text-slate-700 leading-relaxed max-w-3xl mx-auto">
                If you're considering working with me, there are a handful of questions that come up
                almost every time. Here are straightforward answers so you can decide if this is the
                right move for you.
              </p>
            </motion.div>
          </motion.div>

          {/* FAQ Accordion */}
          <motion.div
            variants={accordionVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) => (
                <motion.div key={faq.id} variants={itemVariants}>
                  <AccordionItem
                    value={faq.id}
                    className="rounded-xl border border-slate-200 bg-white/80 px-6 py-2 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <AccordionTrigger className="text-left text-base md:text-lg font-semibold text-slate-900 hover:text-[#0000FF] transition-colors">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm md:text-base text-slate-700 leading-relaxed pt-2 max-w-3xl">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}

